import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  useCreateTemplateForm,
  StepTeacherStudent,
  StepSchedule,
  StepClassInfo,
  StepOther,
  StepSummary,
} from '@/components/admin/class-templates/create-form';

const STEPS = [
  { number: 1, title: 'ครูและนักเรียน' },
  { number: 2, title: 'เวลาเรียน' },
  { number: 3, title: 'ข้อมูลคลาส' },
  { number: 4, title: 'อื่นๆ' },
  { number: 5, title: 'สรุป' },
];

export default function AdminCreateClassTemplatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    currentStep,
    formData,
    updateFormData,
    nextStep,
    prevStep,
    clearStorage,
    isInitialized,
  } = useCreateTemplateForm();

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.teacher_code) {
          toast({ title: 'กรุณาเลือกครู', variant: 'destructive' });
          return false;
        }
        if (formData.students.length === 0) {
          toast({ title: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน', variant: 'destructive' });
          return false;
        }
        return true;
      case 2:
        if (!formData.start_date) {
          toast({ title: 'กรุณาเลือกวันเริ่มเรียน', variant: 'destructive' });
          return false;
        }
        if (formData.schedules.length === 0) {
          toast({ title: 'กรุณาเพิ่มเวลาเรียนอย่างน้อย 1 รายการ', variant: 'destructive' });
          return false;
        }
        return true;
      case 3:
        if (!formData.subject) {
          toast({ title: 'กรุณาเลือกวิชา', variant: 'destructive' });
          return false;
        }
        if (!formData.education_level) {
          toast({ title: 'กรุณาเลือกระดับชั้น', variant: 'destructive' });
          return false;
        }
        return true;
      case 4:
        if (!formData.location) {
          toast({ title: 'กรุณาเลือกสาขา', variant: 'destructive' });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      nextStep();
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-class-templates', {
        body: {
          teacher_code: formData.teacher_code,
          students: formData.students.map(s => ({ code: s.code })),
          schedules: formData.schedules,
          start_date: formData.start_date,
          subject: formData.subject,
          education_level: formData.education_level,
          course_type: formData.course_type,
          class_type: formData.class_type,
          language: formData.language,
          location: formData.location,
          room_name: formData.room_name || null,
          is_special_class: formData.is_special_class,
          fixed_cost: formData.is_special_class ? formData.fixed_cost : null,
          note: formData.note || null,
        },
      });

      if (error) throw error;

      toast({
        title: 'สร้างตารางเรียนสำเร็จ',
        description: `สร้างตารางเรียนทั้งหมด ${data.created_count} รายการ`,
      });

      clearStorage();
      navigate('/admin/class-templates');
    } catch (error: any) {
      console.error('Error creating templates:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถสร้างตารางเรียนได้',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirm('ยกเลิกการสร้างตารางเรียน? ข้อมูลที่กรอกไว้จะถูกลบ')) {
      clearStorage();
      navigate('/admin/class-templates');
    }
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">เพิ่มตารางเรียนใหม่</h1>
          <p className="text-muted-foreground">กรอกข้อมูลตามขั้นตอน</p>
        </div>
        <Button variant="ghost" onClick={handleCancel}>
          ยกเลิก
        </Button>
      </div>

      {/* Steps indicator */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                      ${currentStep === step.number 
                        ? 'bg-primary text-primary-foreground' 
                        : currentStep > step.number 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-muted text-muted-foreground'
                      }`}
                  >
                    {currentStep > step.number ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${currentStep === step.number ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div 
                    className={`w-16 h-0.5 mx-2 ${currentStep > step.number ? 'bg-primary/40' : 'bg-muted'}`} 
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step content */}
      <div className="min-h-[400px]">
        {currentStep === 1 && (
          <StepTeacherStudent formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 2 && (
          <StepSchedule formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 3 && (
          <StepClassInfo formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 4 && (
          <StepOther formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 5 && (
          <StepSummary formData={formData} />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          ย้อนกลับ
        </Button>

        {currentStep < 5 ? (
          <Button onClick={handleNext}>
            ถัดไป
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="min-w-32"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                ยืนยันการสร้าง
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
